import dynamic from 'next/dynamic';
import { Component, useState, type ComponentType, type ReactNode } from 'react';

import type { NangoConnectDialogProps } from './nango-connect-dialog-implementation';
import { setNangoOauthPopupGate } from './open-nango-connect';

export type { ConnectSessionState } from './nango-connect-dialog-implementation';

/** Loads the dialog chunk, or the failure dialog when that import rejects. */
export function loadNangoConnectDialog(
  loadImplementation: () => Promise<ComponentType<NangoConnectDialogProps>> = () =>
    import('./nango-connect-dialog-implementation').then(
      (mod) => mod.NangoConnectDialogImplementation
    )
): Promise<ComponentType<NangoConnectDialogProps>> {
  return loadImplementation().catch(() =>
    import('./nango-connect-dialog-chunk-failed').then(
      (mod) => mod.NangoConnectDialogChunkFailed
    )
  );
}

interface ChunkBoundaryProps {
  flowId: number;
  onAuthFailed: (flowId: number) => void;
  onClose: () => void;
  children: ReactNode;
}

interface ChunkBoundaryState {
  failed: boolean;
}

/** Closes the flow when the dialog chunk throws after the fallback also fails. */
class ConnectChunkBoundary extends Component<ChunkBoundaryProps, ChunkBoundaryState> {
  state: ChunkBoundaryState = { failed: false };
  private reported = false;

  static getDerivedStateFromError(): ChunkBoundaryState {
    return { failed: true };
  }

  componentDidCatch(): void {
    if (this.reported) return;
    this.reported = true;
    setNangoOauthPopupGate(false);
    this.props.onAuthFailed(this.props.flowId);
    this.props.onClose();
  }

  render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

function NangoConnectDialogWhenOpen(props: NangoConnectDialogProps) {
  // A new open builds a new loader, so a failed chunk is tried again.
  const [DialogImpl] = useState(() =>
    dynamic(loadNangoConnectDialog, { ssr: false, loading: () => null })
  );

  return (
    <ConnectChunkBoundary
      flowId={props.flowId}
      onAuthFailed={props.onAuthFailed}
      onClose={props.onClose}
    >
      <DialogImpl {...props} />
    </ConnectChunkBoundary>
  );
}

export function NangoConnectDialog({
  open = true,
  ...props
}: NangoConnectDialogProps & { open?: boolean }) {
  if (!open) return null;
  return <NangoConnectDialogWhenOpen {...props} />;
}
